import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuthStore } from '../store/authStore'
import { communityAPI } from '../services/api'
import type { Post, Comment } from '../types'
import toast from 'react-hot-toast'
import { formatDistanceToNow } from 'date-fns'
import {
  HeartIcon,
  ChatBubbleLeftIcon,
  EyeIcon,
  FireIcon,
  RocketLaunchIcon,
} from '@heroicons/react/24/outline'
import { HeartIcon as HeartIconSolid } from '@heroicons/react/24/solid'
import ConfirmDialog from '../components/ConfirmDialog'
import ImageUpload from '../components/ImageUpload'
import LoadingScreen from '../components/LoadingScreen'
import Tooltip from '../components/Tooltip'
import { FEED_GLOSSARY } from '../config/glossary'
import { callPath } from '../routes'
import { mediaUrl } from '../utils/mediaUrl'

type FeedType = 'following' | 'discover' | 'trending'

/** What each tab shows when it is empty: what it reads, not a blanket "no posts". */
export const EMPTY_FEED: Record<FeedType, string> = {
  following: 'Nothing here yet. Follow creators, or post something yourself.',
  discover: "No posts from accounts you don't follow yet.",
  trending: 'Nothing has picked up likes or comments in the last 7 days.',
}

const Feed = () => {
  const [posts, setPosts] = useState<Post[]>([])
  const [loading, setLoading] = useState(true)
  const [feedType, setFeedType] = useState<FeedType>('following')
  const [newPostContent, setNewPostContent] = useState('')
  const [newPostImages, setNewPostImages] = useState<string[]>([])
  const [showNewPost, setShowNewPost] = useState(false)
  const [creatingPost, setCreatingPost] = useState(false)
  const { user } = useAuthStore()

  useEffect(() => {
    const loadFeed = async () => {
      try {
        setLoading(true)
        const feedData = await communityAPI.getFeed({ feed_type: feedType, limit: 50 })
        setPosts(feedData)
      } catch (error) {
        console.error('Error loading feed:', error)
        toast.error('Failed to load feed')
      } finally {
        setLoading(false)
      }
    }
    loadFeed()
  }, [feedType])

  const handleCreatePost = async () => {
    if (!newPostContent.trim()) {
      toast.error('Post content cannot be empty')
      return
    }

    try {
      setCreatingPost(true)
      const newPost = await communityAPI.createPost({
        content: newPostContent,
        post_type: 'STATUS',
        images: newPostImages.length > 0 ? newPostImages : undefined,
      })
      setPosts([newPost, ...posts])
      setNewPostContent('')
      setNewPostImages([])
      setShowNewPost(false)
      toast.success('Post created successfully!')
    } catch (error) {
      console.error('Error creating post:', error)
      toast.error('Failed to create post')
    } finally {
      setCreatingPost(false)
    }
  }

  const handleLikePost = async (postId: number, currentlyLiked: boolean) => {
    try {
      if (currentlyLiked) {
        await communityAPI.removeReaction(postId)
      } else {
        await communityAPI.addReaction(postId, 'LIKE')
      }

      // Update local state
      setPosts(
        posts.map((post) =>
          post.id === postId
            ? {
                ...post,
                is_liked: !currentlyLiked,
                like_count: currentlyLiked ? post.like_count - 1 : post.like_count + 1,
              }
            : post
        )
      )
    } catch (error) {
      console.error('Error toggling like:', error)
      toast.error('Failed to update like')
    }
  }

  if (loading) {
    return <LoadingScreen />
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">Community Feed</h1>
        <button
          onClick={() => setShowNewPost(!showNewPost)}
          className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition"
        >
          {showNewPost ? 'Cancel' : 'New Post'}
        </button>
      </div>

      {/* Feed Type Tabs */}
      <div className="bg-white rounded-lg shadow">
        <div className="flex border-b">
          <button
            onClick={() => setFeedType('following')}
            className={`flex-1 py-3 px-4 font-medium transition ${
              feedType === 'following'
                ? 'text-primary-600 border-b-2 border-primary-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Following
          </button>
          <button
            onClick={() => setFeedType('discover')}
            className={`flex-1 py-3 px-4 font-medium transition ${
              feedType === 'discover'
                ? 'text-primary-600 border-b-2 border-primary-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Discover
          </button>
          <button
            onClick={() => setFeedType('trending')}
            className={`flex-1 py-3 px-4 font-medium transition ${
              feedType === 'trending'
                ? 'text-primary-600 border-b-2 border-primary-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Trending
          </button>
          <div className="flex items-center px-3">
            <Tooltip content={FEED_GLOSSARY.tabs} label="the feed filter" />
          </div>
        </div>
      </div>

      {/* New Post Form */}
      {showNewPost && (
        <div className="bg-white rounded-lg shadow p-6 space-y-4">
          <textarea
            value={newPostContent}
            onChange={(e) => setNewPostContent(e.target.value)}
            placeholder="What's on your mind?"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            rows={4}
          />

          {/* Image Upload */}
          <ImageUpload
            category="posts"
            maxFiles={4}
            multiple={true}
            onUploadComplete={(urls) => setNewPostImages(urls)}
            className="border-t pt-4"
          />

          {/* Display selected images count */}
          {newPostImages.length > 0 && (
            <div className="text-sm text-gray-600">
              {newPostImages.length} image(s) attached
            </div>
          )}

          <div className="flex justify-end mt-4 space-x-3">
            <button
              onClick={() => {
                setShowNewPost(false)
                setNewPostImages([])
              }}
              className="px-4 py-2 text-gray-700 hover:text-gray-900"
              disabled={creatingPost}
            >
              Cancel
            </button>
            <button
              onClick={handleCreatePost}
              disabled={creatingPost || !newPostContent.trim()}
              className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {creatingPost ? 'Posting...' : 'Post'}
            </button>
          </div>
        </div>
      )}

      {/* Posts Feed */}
      <div className="space-y-4">
        {posts.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center" data-testid="feed-empty">
            <p className="text-gray-500">{EMPTY_FEED[feedType]}</p>
          </div>
        ) : (
          posts.map((post) => (
            <PostCard
              key={post.id}
              post={post}
              onLike={handleLikePost}
              currentUserId={user?.id}
            />
          ))
        )}
      </div>
    </div>
  )
}

interface PostCardProps {
  post: Post
  onLike: (postId: number, currentlyLiked: boolean) => void
  currentUserId?: number
}

const PostCard = ({ post, onLike, currentUserId }: PostCardProps) => {
  const [showComments, setShowComments] = useState(false)
  const [comments, setComments] = useState<Comment[]>([])
  const [commentsLoaded, setCommentsLoaded] = useState(false)
  const [commentsLoading, setCommentsLoading] = useState(false)
  const [newComment, setNewComment] = useState('')
  const [postingComment, setPostingComment] = useState(false)
  const [commentCount, setCommentCount] = useState(post.comment_count)
  const [deletingCommentId, setDeletingCommentId] = useState<number | null>(null)
  const [deletingComment, setDeletingComment] = useState(false)

  const loadComments = async () => {
    try {
      setCommentsLoading(true)
      const data = await communityAPI.getComments(post.id)
      setComments(data)
      setCommentsLoaded(true)
    } catch (error) {
      console.error('Error loading comments:', error)
      toast.error('Failed to load comments')
    } finally {
      setCommentsLoading(false)
    }
  }

  const handleToggleComments = () => {
    const next = !showComments
    setShowComments(next)
    if (next && !commentsLoaded) {
      loadComments()
    }
  }

  const handleAddComment = async () => {
    if (!newComment.trim()) return

    try {
      setPostingComment(true)
      const comment = await communityAPI.addComment(post.id, newComment.trim())
      setComments((prev) => [...prev, comment])
      setCommentCount((prev) => prev + 1)
      setNewComment('')
    } catch (error) {
      console.error('Error adding comment:', error)
      toast.error('Failed to post comment')
    } finally {
      setPostingComment(false)
    }
  }

  const handleDeleteComment = async () => {
    if (deletingCommentId === null) return

    setDeletingComment(true)
    try {
      await communityAPI.deleteComment(deletingCommentId)
      setComments((prev) => prev.filter((comment) => comment.id !== deletingCommentId))
      setCommentCount((prev) => Math.max(0, prev - 1))
      setDeletingCommentId(null)
    } catch (error) {
      console.error('Error deleting comment:', error)
      toast.error('Failed to delete comment')
    } finally {
      setDeletingComment(false)
    }
  }

  const getPostTypeStyle = (type: string) => {
    switch (type) {
      case 'SIGNAL':
        return 'bg-success-50 text-success-700'
      case 'PORTFOLIO':
        return 'bg-primary-50 text-primary-700'
      case 'ACHIEVEMENT':
        return 'bg-warning-50 text-warning-700'
      default:
        return 'bg-gray-50 text-gray-700'
    }
  }

  const getPostTypeIcon = (type: string) => {
    switch (type) {
      case 'SIGNAL':
        return <FireIcon className="h-4 w-4" />
      case 'PORTFOLIO':
        return <RocketLaunchIcon className="h-4 w-4" />
      default:
        return null
    }
  }

  return (
    <div className="bg-white rounded-lg shadow hover:shadow-md transition">
      <div className="p-6">
        {/* Post Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="h-12 w-12 rounded-full bg-primary-100 flex items-center justify-center">
              <span className="text-primary-600 font-semibold text-lg">
                {post.user?.username?.[0]?.toUpperCase() || 'U'}
              </span>
            </div>
            <div>
              <p className="font-semibold text-gray-900">
                {post.user_profile?.display_name || post.user?.username || 'Unknown User'}
              </p>
              <p className="text-sm text-gray-500">
                {formatDistanceToNow(new Date(post.created_at), { addSuffix: true })}
              </p>
            </div>
          </div>
          <span
            className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${getPostTypeStyle(
              post.post_type
            )}`}
          >
            {getPostTypeIcon(post.post_type)}
            {post.post_type.toUpperCase()}
          </span>
        </div>

        {/* Post Content */}
        <div className="mb-4">
          <p className="text-gray-900 whitespace-pre-wrap">{post.content}</p>
          {post.post_type === 'SIGNAL' && post.signal_id != null && (
            <Link
              to={callPath(post.signal_id)}
              className="mt-2 inline-block text-sm font-medium text-primary-600 hover:text-primary-700"
            >
              View the signal →
            </Link>
          )}
        </div>

        {/* Post Images */}
        {post.images && post.images.length > 0 && (
          <div className="mb-4 grid grid-cols-2 gap-2">
            {post.images.map((image, index) => (
              <img
                key={index}
                src={mediaUrl(image)}
                alt={`Post attachment ${index + 1}`}
                className="rounded-lg w-full h-48 object-cover"
              />
            ))}
          </div>
        )}

        {/* Post Actions */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
          <div className="flex items-center space-x-6">
            <button
              onClick={() => onLike(post.id, post.is_liked || false)}
              className="flex items-center space-x-2 text-gray-500 hover:text-primary-600 transition"
            >
              {post.is_liked ? (
                <HeartIconSolid className="h-5 w-5 text-primary-600" />
              ) : (
                <HeartIcon className="h-5 w-5" />
              )}
              <span className="text-sm font-medium">{post.like_count}</span>
            </button>

            <button
              onClick={handleToggleComments}
              className="flex items-center space-x-2 text-gray-500 hover:text-primary-600 transition"
            >
              <ChatBubbleLeftIcon className="h-5 w-5" />
              <span className="text-sm font-medium">{commentCount}</span>
            </button>

            <div className="flex items-center space-x-2 text-gray-500">
              <EyeIcon className="h-5 w-5" />
              <span className="text-sm font-medium">{post.view_count}</span>
            </div>
          </div>
        </div>

        {/* Comments Section (collapsible) */}
        {showComments && (
          <div className="mt-4 pt-4 border-t border-gray-100 space-y-4">
            <div className="flex items-start space-x-3">
              <div className="h-9 w-9 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                <span className="text-primary-600 font-semibold text-sm">U</span>
              </div>
              <div className="flex-1 flex items-center space-x-2">
                <input
                  type="text"
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault()
                      handleAddComment()
                    }
                  }}
                  placeholder="Write a comment..."
                  className="flex-1 px-4 py-2 border border-gray-200 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                <button
                  onClick={handleAddComment}
                  disabled={postingComment || !newComment.trim()}
                  className="px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-full hover:bg-primary-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {postingComment ? 'Posting…' : 'Post'}
                </button>
              </div>
            </div>

            {commentsLoading ? (
              <p className="text-sm text-gray-500 text-center py-2">Loading comments…</p>
            ) : comments.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-2">
                No comments yet. Be the first to comment!
              </p>
            ) : (
              <div className="space-y-3">
                {comments.map((comment) => (
                  <div key={comment.id} className="flex items-start space-x-3">
                    <div className="h-9 w-9 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-gray-600 font-semibold text-sm">
                        {comment.user?.username?.[0]?.toUpperCase() || 'U'}
                      </span>
                    </div>
                    <div className="flex-1">
                      <div className="bg-gray-50 rounded-2xl px-4 py-2">
                        <p className="text-sm font-semibold text-gray-900">
                          {comment.user_profile?.display_name || comment.user?.username || 'Unknown User'}
                        </p>
                        <p className="text-sm text-gray-700 whitespace-pre-wrap">{comment.content}</p>
                      </div>
                      <div className="flex items-center space-x-3 mt-1 px-4">
                        <span className="text-xs text-gray-500">
                          {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
                        </span>
                        {currentUserId === comment.user_id && (
                          <button
                            onClick={() => setDeletingCommentId(comment.id)}
                            className="text-xs text-gray-500 hover:text-red-600 transition"
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <ConfirmDialog
        isOpen={deletingCommentId !== null}
        title="Delete comment"
        message="Are you sure you want to delete this comment? This action cannot be undone."
        confirmLabel="Delete"
        confirming={deletingComment}
        danger
        onConfirm={handleDeleteComment}
        onCancel={() => setDeletingCommentId(null)}
      />
    </div>
  )
}

export default Feed
